import React, { ReactNode, useState, useEffect } from 'react';
import produce from "immer";
import { Toaster } from 'react-hot-toast';

import './Home.scss';
import { DeleteModal, RepeatModal, ErrorMessage, Page, PageHeader } from 'components';
import { LoadState } from 'enums';
import { ICapture, IReplay } from 'interfaces';
import { CaptureItem } from './items/CaptureItem';
import { CaptureModal } from './modals/CaptureModal';
import { Button, Col, LoadingIndicator, Modal, Row, TabItem, TabPanel, Tabs, TextField } from 'cfa-react-components';
import { CaptureConfigService, CaptureService, MessageService, ReplayConfigService, ReplayService } from 'services';
import { ReplayItem } from './items/ReplayItem';
import { ReplayModal } from './modals/ReplayModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MessageModal } from './modals/MessageModal';

interface IHomePageState {
    // load state
    loadStatus: LoadState;
    loadError?: string;
    // modals
    showCaptureModal?: boolean;
    showReplayModal?: boolean;
    selectedCapture?: ICapture;
    showMessageModal?: boolean;
    selectedReplay?: IReplay;
    // delete confirmation
    showDeleteModal?: boolean;
    deleteTarget?: any,
    deleteFunc?: (any) => Promise<any>;
    deleteVerb?: string;
    deleteNoun?: string;
    // repeat confirmation
    showRepeatModal?: boolean;
    repeatTarget?: any,
    repeatFunc?: (any) => Promise<any>;
    repeatNoun?: string;
}

export function Home() {

    const [pageState, setPageState] = useState<IHomePageState>({
        loadStatus: LoadState.Loading,
    });
    const { loadStatus, loadError, showCaptureModal, showReplayModal, selectedCapture, selectedReplay, showMessageModal, 
        showDeleteModal, deleteTarget, deleteFunc, deleteVerb, deleteNoun,
        showRepeatModal, repeatTarget, repeatFunc, repeatNoun} = pageState;
    
    const [captures, setCaptures] = useState<ICapture[]>([]);
    const [replays, setReplays] = useState<IReplay[]>([]);

    const [selectedTabItemKey, setSelectedTabItemKey] = useState("capture");

    useEffect(() => {
        if (loadStatus === LoadState.Success) return;

        Promise.all([
            CaptureService.getAll(),
            ReplayService.getAll(),
        ]).then(() => {
            setCaptures(CaptureService.allCaptures);
            setReplays(ReplayService.allReplays);
            setPageState(produce(draft => {
                draft.loadStatus = LoadState.Success;
            }));
        });
    });

    // Capture modal
    const openCaptureModal = () => {
        setPageState(produce(draft => {
            draft.showCaptureModal = true;
        }))
    }

    const repeatCaptureModal = (cap: ICapture) => {
        setPageState(produce(draft => {
            draft.selectedCapture = cap;
            draft.showCaptureModal = true;
        }))
    }

    const closeCaptureModal = () => {
        setPageState(produce(draft => {
            draft.showCaptureModal = false;
            draft.selectedCapture = undefined;
        }))
    }

    // Replay modal
    const openReplayModal = (cap: ICapture) => {
        setPageState(produce(draft => {
            draft.selectedCapture = cap;
            draft.showReplayModal = true;
        }))
    }

    const closeReplayModal = () => {
        setPageState(produce(draft => {
            draft.selectedCapture = undefined;
            draft.showReplayModal = false;
        }))
    }

    // Replay events modal
    const openMessageModal = (rep: IReplay) => {
        setPageState(produce(draft => {
            draft.selectedReplay = rep;
            draft.showMessageModal = true;
        }))
    }

    const closeMessageModal = () => {
        // prevent further messages
        MessageService.disconnect();

        setPageState(produce(draft => {
            draft.selectedReplay = undefined;
            draft.showMessageModal = false;
        }))
    }

    // delete modal
    const openDeleteModal = (target: any, deleteFunc: (any) => Promise<any>, deleteVerb: string, deleteNoun: string) => {
        setPageState(produce(draft => {
            draft.showDeleteModal = true;
            draft.deleteTarget = target;
            draft.deleteFunc = deleteFunc;
            draft.deleteVerb = deleteVerb;
            draft.deleteNoun = deleteNoun;
        }))
    }

    const closeDeleteModal = () => {
        setPageState(produce(draft => {
            draft.showDeleteModal = false;
            draft.deleteTarget = undefined;
            draft.deleteFunc = undefined;
            draft.deleteVerb = undefined;
            draft.deleteNoun = undefined;
        }))
    }

    // repeat modal
    const openRepeatModal = (target: any, repeatFunc: (any) => Promise<any>, repeatNoun: string) => {
        setPageState(produce(draft => {
            draft.showRepeatModal = true;
            draft.repeatTarget = target;
            draft.repeatFunc = repeatFunc;
            draft.repeatNoun = repeatNoun;
        }))
    }

    const closeRepeatModal = () => {
        setPageState(produce(draft => {
            draft.showRepeatModal = false;
            draft.repeatTarget = undefined;
            draft.repeatFunc = undefined;
            draft.repeatNoun = undefined;
        }))
    }

    // refresh application data
    const refreshData = () => {
        setCaptures([]);
        setReplays([]);
        // set to loading here to refresh using the useEffect above
        setPageState(produce(draft => {
            draft.loadStatus = LoadState.Loading;
        }));
    }

    // refresh list
    const refresh = () => {
        setCaptures(CaptureService.allCaptures);
        setReplays(ReplayService.allReplays);
    }

    // filter capture list
    const filterCaptures = (e) => {
        let text = e.target.value.toLowerCase();
        let caps = CaptureService.allCaptures.filter(c => {
            // search capture name
            if (c.name.toLowerCase().indexOf(text) != -1) return c;
            // search config name and source type
            let cfg = CaptureConfigService.allCaptureConfigsMap[c.configUUID];
            if (cfg.name.toLowerCase().indexOf(text) != -1) return c;
            if (cfg.sourceType.toLowerCase().indexOf(text) != -1) return c;
        });

        setCaptures(caps);
    }

    // filter replay list
    const filterReplays = (e) => {
        let text = e.target.value.toLowerCase();
        let reps = ReplayService.allReplays.filter(r => {
            // search replay name
            if (r.name.toLowerCase().indexOf(text) != -1) return r;
            // search config name and sink type
            let cfg = ReplayConfigService.allReplayConfigsMap[r.configUUID];
            if (cfg.name.toLowerCase().indexOf(text) != -1) return r;
            if (cfg.sinkType.toLowerCase().indexOf(text) != -1) return r;
        });

        setReplays(reps);
    }

    // Content
    const pageHeader: ReactNode = (
        <Row style={{float: "right"}}>
            <Button className="refreshButton" loading={loadStatus == LoadState.Loading} variant="outlined" onClick={refreshData}>
                <FontAwesomeIcon icon="redo" className="refreshIcon" />
            </Button>
            {selectedTabItemKey === "capture" && (
                <Button onClick={openCaptureModal}>{'Start Capture'}</Button>
                )}
            {selectedTabItemKey === "replay" && (
                <Button onClick={() => openReplayModal(undefined)}>{'Start Replay'}</Button>
            )}
        </Row>
    );

    const captureTableHeaders = (captures && captures.length > 0 && (
        <tr className="itemTable_headers">
            <th className="itemTable_header">Capture Name</th>
            <th className="itemTable_header">Config</th>
            <th className="itemTable_header">Start Time</th>
            <th className="itemTable_header">End Time</th>
            <th className="itemTable_header">Message Count</th>
            <th className="itemTable_header">Message Expiration</th>
            <th className="itemTable_header"></th>
        </tr>
    ));

    let captureTableContent: ReactNode;
    switch (loadStatus) {
        case LoadState.Initial:
        case LoadState.Loading:
        case LoadState.Success:
            if (captures.length > 0 || loadStatus === LoadState.Loading) {
                captureTableContent = (<>
                    {captures.map(capture => (
                        <CaptureItem 
                            key={capture.captureUUID}
                            capture={capture}
                            openRepeatModal={repeatCaptureModal}
                            openReplayModal={openReplayModal}
                            openDeleteModal={openDeleteModal}
                        />
                    ))}
                    {loadStatus === LoadState.Loading && (
                        <LoadingIndicator size="md" variant="inline" style={{margin: "24px"}}/>
                    )}
                </>);
            } else {
                captureTableContent = (
                    <tr><td className='itemTable_item'>No captures found</td></tr>
                );
            }
            break;
        case LoadState.Error:
            captureTableContent = (
                <ErrorMessage message={loadError || 'Error loading.'} />
            );
            break;
    }

    const replayTableHeaders = (replays && replays.length > 0 && (
        <tr className="itemTable_headers">
            <th className="itemTable_header">Replay Name</th>
            <th className="itemTable_header">Config</th>
            <th className="itemTable_header">Start Time</th>
            <th className="itemTable_header">End Time</th>
            <th className="itemTable_header">Capture UUID</th>
            <th className="itemTable_header"></th>
        </tr>
    ));

    let replayTableContent: ReactNode;
    switch (loadStatus) {
        case LoadState.Initial:
        case LoadState.Loading:
        case LoadState.Success:
            if (replays.length > 0 || loadStatus === LoadState.Loading) {
                replayTableContent = (<>
                    {replays.map(replay => (
                        <ReplayItem 
                            key={replay.replayUUID}
                            replay={replay}
                            openDeleteModal={openDeleteModal}
                            openReplayModal={openRepeatModal}
                            openMessageModal={openMessageModal}
                        />
                    ))}
                    {loadStatus === LoadState.Loading && (
                        <LoadingIndicator size="md" variant="inline" style={{margin: "24px"}}/>
                    )}
                </>);
            } else {
                replayTableContent = (
                    <tr><td className='itemTable_item'>No replays found</td></tr>
                );
            }
            break;
        case LoadState.Error:
            replayTableContent = (
                <ErrorMessage message={loadError || 'Error loading.'} />
            );
            break;
    }

    // page tabs
    const pageTabs: ReactNode = (
        <Tabs activeItemKey={selectedTabItemKey} onChange={(i) => {
            setSelectedTabItemKey(i);
            refresh(); // reset filter
        }}>
            <TabItem itemKey='capture'>
                Captures
            </TabItem>
            <TabItem itemKey='replay'>
                Replays
            </TabItem>
        </Tabs>
    );

    return (<>
        <Page>
            <PageHeader title={'Home'} right={pageHeader}/>
            {pageTabs}
            <Row>
                <Col>
                    {selectedTabItemKey == "capture" && <TextField
                        label={'Filter'}
                        placeholder='Enter capture name, config name, or source type...'
                        onChange={filterCaptures}
                    />}
                    {selectedTabItemKey == "replay" && <TextField
                        label={'Filter'} 
                        placeholder='Enter replay name, config name, or sink type...'
                        onChange={filterReplays}
                    />}
                </Col>
            </Row>
            <TabPanel itemKey='capture' activeItemKey={selectedTabItemKey}>
                <table className="itemTable">
                    <thead>{captureTableHeaders}</thead>
                    <tbody>{captureTableContent}</tbody>
                </table>
            </TabPanel>
            <TabPanel itemKey='replay' activeItemKey={selectedTabItemKey}>
                <table className="itemTable">
                    <thead>{replayTableHeaders}</thead>
                    <tbody>{replayTableContent}</tbody>
                </table>
            </TabPanel>
        </Page>

        <Toaster />
        
        {/* wrap components in modal so that the contents are unmounted on close */}
        <Modal show={showCaptureModal} onClose={closeCaptureModal} size='lg'>
            <CaptureModal 
                key={0} 
                captureToRepeat={selectedCapture}
                onHide={closeCaptureModal} 
                refresh={refresh}
            />
        </Modal>
        
        <Modal show={showReplayModal} onClose={closeReplayModal} size='lg'>
            <ReplayModal 
                key={1} 
                selectedCapture={selectedCapture} 
                refresh={refresh} 
                onHide={closeReplayModal}
            />
        </Modal>

        <Modal show={showMessageModal} onClose={closeMessageModal} size='lg' disableOverlayClick={true}>
            <MessageModal 
                key={1} 
                selectedReplay={selectedReplay}
                onHide={closeMessageModal}
            />
        </Modal>

        <Modal show={showDeleteModal} onClose={closeDeleteModal} size='sm'>
            <DeleteModal 
                key={2} 
                target={deleteTarget}
                targetFunc={deleteFunc} 
                onHide={closeDeleteModal} 
                refresh={refresh} 
                deleteVerb={deleteVerb} 
                deleteNoun={deleteNoun}
            />
        </Modal>

        <Modal show={showRepeatModal} onClose={closeRepeatModal} size='sm'>
            <RepeatModal
                key={2}
                target={repeatTarget}
                targetFunc={repeatFunc}
                onHide={closeRepeatModal}
                refresh={refresh}
                repeatNoun={repeatNoun}
            />
        </Modal>
    </>);
}
